import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setCartData, setAddressValidity } from "@/store/cart/cartSlice";
import {
    changeCountryApi, getAddressByPincodeApi,
    validatePhoneApi, chatApi, changeCurrentAddressApi
} from "@/clientSideApi/core";
import { getCartDataApi, submitAddressFormApi } from "@/clientSideApi/cart";
import { setExpertInfo, setUtmInfo } from "@/store/general/generalSlice";
import {
    DEFAULT_ADDRESS_FORMAT, DEFAULT_COUNTRY,
    DEFAULT_COUNTRY_CODE
} from "@/constants/default_country";

let lastPhoneInput;
let lastPincodeInput;

const useAddress = () => {
    const dispatch = useDispatch();
    const { expertInfo, utm } = useSelector((state) => state.general);
    const shipping_address = useSelector((state) => state.cart.data?.shipping_address);
    const is_other_addresses_available = useSelector((state) => state.cart.data?.is_other_addresses_available);
    const [addressFormat, setAddressFormat] = useState(DEFAULT_ADDRESS_FORMAT.fields);
    const [countryCodeDropdown, setCountryCodeDropdown] = useState(false);
    const [chooseAddressPopup, setChooseAddressPopup] = useState(false);
    const [showAddAddress, setShowAddAddress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(false);
    const [formData, setFormData] = useState({
        country: shipping_address ? shipping_address.country : DEFAULT_COUNTRY,
        phone_0: shipping_address ? shipping_address.phone_0 : DEFAULT_COUNTRY_CODE,
        phone_1: shipping_address ? shipping_address.phone_1 : "",
        full_name: shipping_address ? shipping_address.full_name : "",
        street_address: shipping_address ? shipping_address.street_address : "",
        city: shipping_address ? shipping_address.city : "",
        pin_code: shipping_address ? shipping_address.pin_code : "",
        city_area: shipping_address ? shipping_address.city_area : "",
        country_area: shipping_address ? shipping_address.country_area : "",
    })

    const handleChangeFormData = useCallback((label, val) => {
        setFormData(prevData => ({ ...prevData, [label]: val }))
    }, []);

    const changeChooseAddress = useCallback((val) => {
        setChooseAddressPopup(val);
    }, []);

    const toggleAddAddress = useCallback(({ value, shippingData, addressValid}) => {
        if(shippingData) setFormData(shippingData);
        setShowAddAddress(value);
        changeChooseAddress(false);
        if(addressValid !== null) dispatch(setAddressValidity(addressValid));
    }, [ dispatch, changeChooseAddress ]);

    const toggleCountryCode = useCallback((val) => {
        setCountryCodeDropdown(val);
    }, []);

    const handleChangeCountry = useCallback(async (label, val) => {
        let newAddressFormat
        if (val[0] !== DEFAULT_COUNTRY[0]) {
            const apiResponse = await changeCountryApi(val[0]);
            if (!apiResponse || !apiResponse.fields) return;
            newAddressFormat = apiResponse.fields
        } else {
            newAddressFormat = DEFAULT_ADDRESS_FORMAT.fields
        }
        handleChangeFormData(label, val);
        setFormData(prevData => ({
            ...prevData, street_address: "", city: "",
            pin_code: "", city_area: "", country_area: ""
        }));
        setAddressFormat(newAddressFormat)
    }, [ handleChangeFormData ])

    const handleChangeCountryCode = (val) => {
        handleChangeFormData('phone_0', val);
        toggleCountryCode(false);
        phoneNumberChange(val)
    };

    const pincodeFocus = () => {
        lastPincodeInput = formData.pin_code;
        setErrorMsg((prevErr) => {
            if (!prevErr) return null;
            const erros = Object.keys(prevErr);
            if (erros.length === 1 && erros[0] === "postal_code") return null;
            const { postal_code, ...rest } = prevErr;
            return rest;
        });
    };

    const pincodeChange = async () => {
        if (!formData.pin_code.length) return;
        if (formData.pin_code === lastPincodeInput) return;
        setLoading(true);
        const data = await getAddressByPincodeApi(formData.pin_code, true);
        setLoading(false);
        if (!data) return;

        if (data.city) {
            setFormData((prevData) => ({
                ...prevData,
                city: data.city,
            }));
        }

        if (data.state) {
            setFormData((prevData) => ({
                ...prevData,
                country_area: data.state,
            }));
        }

        if (data.city_area) {
            setFormData((prevData) => ({
                ...prevData,
                city_area: data.city_area,
            }));
        }
        setErrorMsg((prevErr) => {
            if (!prevErr) return null;
            const erros = Object.keys(prevErr);
            if (erros.length === 1 && erros[0] === "postal_code") return null;
            const { postal_code, ...rest } = prevErr;
            return rest;
        });
    };

    const phoneNumberFocus = () => {
        lastPhoneInput = formData.phone_1;
        setErrorMsg((prevErr) => {
            if (!prevErr) return null;
            const erros = Object.keys(prevErr);
            if (erros.length === 1 && erros[0] === "phone") return null;
            const { phone, ...rest } = prevErr;
            return rest;
        });
    };

    const phoneNumberChange = async (countryCode) => {
        if (!formData.phone_1.length) return;
        if (formData.phone_1 === lastPhoneInput) return;
        setLoading(true);
        const isValid = await validatePhoneApi(
            { phone: countryCode + formData.phone_1 },
            true
        );
        setLoading(false);
        if (!isValid) {
            setErrorMsg((prevErr) => ({
                ...prevErr,
                phone: ["Enter a valid phone"],
            }));
        } else {
            setErrorMsg((prevErr) => {
                if (!prevErr) return null;
                const erros = Object.keys(prevErr);
                if (erros.length === 1 && erros[0] === "phone") return null;
                const { phone, ...rest } = prevErr;
                return rest;
            });
        }
    };

    const onSubmitForm = async () => {
        if (loading) return;

        if (!/^\d+$/.test(formData.phone_1) || (errorMsg && errorMsg.phone)) {
            setErrorMsg(prevErr => ({ ...prevErr, phone: [errorMsg?.phone || "Enter a valid phone number"] }))
            return;
        }

        if (!formData.pin_code || (errorMsg && errorMsg.postal_code)) {
            setErrorMsg(prevErr => (
                { ...prevErr, postal_code: [
                    errorMsg?.postal_code && errorMsg?.postal_code[0]  || 
                    "Enter a valid postal code"
                ]}
            ))
            return;
        }

        setLoading(true);
        setErrorMsg(false);
        //update address in cart if its currently selected or new address//
        const { full_name, street_address, pin_code, ...rest } = formData;
        const { success, error } = await submitAddressFormApi({
            ...rest,
            first_name: full_name,
            last_name: "",
            country: formData.country[0],
            street_address_1: street_address,
            street_address_2: "",
            postal_code: pin_code,
        });
        if (success) {
            const cartApiResponse = await getCartDataApi();
            if (cartApiResponse) {
                dispatch(setCartData(cartApiResponse));
                toggleAddAddress({ 
                    value: false, 
                    shippingData : cartApiResponse.shipping_address,
                    addressValid: null
                });
            }
            if (!expertInfo) {
                const url = "/api/rest/v1/chat/" + (utm ? '?' + utm : '');
                const phone = formData.phone_0 + formData.phone_1;
                const chatApiResponse = await chatApi(url, { phone });
                if (chatApiResponse) {
                    const raw_data = JSON.stringify(chatApiResponse);
                    localStorage.setItem("expert_info", raw_data);
                    dispatch(setExpertInfo(chatApiResponse));
                    dispatch(setUtmInfo(null));
                };
            }
        }
        if (error) {
            setErrorMsg(error)
        }
        setLoading(false);
    };

    const handleSelectShippingAddress = useCallback(async (pk) => {
        if (!pk) return;
        const apiResponse = await changeCurrentAddressApi(pk);
        if (apiResponse) {
            const cartApiResponse = await getCartDataApi();
            if (cartApiResponse) {
                dispatch(setCartData(cartApiResponse));
                changeChooseAddress(false);
            }
        }
    }, [ dispatch, changeChooseAddress ])

    return {
        loading,
        formData,
        errorMsg,
        addressFormat,
        countryCodeDropdown,
        shipping_address,
        showAddAddress,
        is_other_addresses_available,
        chooseAddressPopup,
        changeChooseAddress,
        toggleAddAddress,
        handleChangeCountry,
        handleChangeFormData,
        pincodeChange,
        pincodeFocus,
        handleChangeCountryCode,
        toggleCountryCode,
        phoneNumberChange,
        phoneNumberFocus,
        onSubmitForm,
        handleSelectShippingAddress
    }
}

export default useAddress